import { Injectable, OnDestroy } from "@angular/core";
import { MatPaginatorIntl } from "@angular/material/paginator";
import { TranslateService } from "@ngx-translate/core";
import { Subscription } from "rxjs";

const KEYS = {
  itemsPerPage: "shared.paginator.itemsPerPage",
  nextPage: "shared.paginator.nextPage",
  previousPage: "shared.paginator.previousPage",
  firstPage: "shared.paginator.firstPage",
  lastPage: "shared.paginator.lastPage",
  rangeEmpty: "shared.paginator.rangeEmpty",
  range: "shared.paginator.range"
};

/**
 * MatPaginatorIntl backed by ngx-translate. Labels follow the active language;
 * until translations are loaded, Material's built-in English defaults are kept.
 */
@Injectable()
export class TranslatedPaginatorIntl extends MatPaginatorIntl implements OnDestroy {
  private readonly materialDefaults = {
    itemsPerPageLabel: this.itemsPerPageLabel,
    nextPageLabel: this.nextPageLabel,
    previousPageLabel: this.previousPageLabel,
    firstPageLabel: this.firstPageLabel,
    lastPageLabel: this.lastPageLabel,
    getRangeLabel: this.getRangeLabel
  };

  private readonly langSub: Subscription;

  constructor(private translate: TranslateService) {
    super();
    this.getRangeLabel = (page: number, pageSize: number, length: number) =>
      this.rangeLabel(page, pageSize, length);
    this.refreshLabels();
    this.langSub = this.translate.onLangChange.subscribe(() => {
      this.refreshLabels();
      this.changes.next();
    });
  }

  ngOnDestroy(): void {
    this.langSub.unsubscribe();
  }

  private refreshLabels(): void {
    const d = this.materialDefaults;
    this.itemsPerPageLabel = this.label(KEYS.itemsPerPage, d.itemsPerPageLabel);
    this.nextPageLabel = this.label(KEYS.nextPage, d.nextPageLabel);
    this.previousPageLabel = this.label(KEYS.previousPage, d.previousPageLabel);
    this.firstPageLabel = this.label(KEYS.firstPage, d.firstPageLabel);
    this.lastPageLabel = this.label(KEYS.lastPage, d.lastPageLabel);
  }

  /** Same arithmetic as Material's default getRangeLabel. */
  private rangeLabel(page: number, pageSize: number, length: number): string {
    if (length == 0 || pageSize == 0) {
      return this.label(KEYS.rangeEmpty, null, { length: String(length) }) ??
        this.materialDefaults.getRangeLabel(page, pageSize, length);
    }
    length = Math.max(length, 0);
    const startIndex = page * pageSize;
    // If the start index exceeds the list length, do not try and fix the end index to the end.
    const endIndex =
      startIndex < length
        ? Math.min(startIndex + pageSize, length)
        : startIndex + pageSize;
    return this.label(KEYS.range, null, {
      start: String(startIndex + 1),
      end: String(endIndex),
      length: String(length)
    }) ?? this.materialDefaults.getRangeLabel(page, pageSize, length);
  }

  /** Translated text, or `fallback` while the key is not available. */
  private label(
    key: string,
    fallback: string | null,
    params?: Record<string, string>
  ): string | null {
    const value = this.translate.instant(key, params);
    return typeof value === "string" && value !== key ? value : fallback;
  }
}
